import { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

let paystackScriptPromise = null;

const ensurePaystackScript = () => {
    if (typeof window === "undefined") {
        return Promise.resolve(false);
    }

    if (window.PaystackPop) {
        return Promise.resolve(true);
    }

    if (!paystackScriptPromise) {
        paystackScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://js.paystack.co/v1/inline.js";
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => reject(new Error("Failed to load Paystack SDK"));
            document.body.appendChild(script);
        });
    }

    return paystackScriptPromise.catch(() => false);
};

const buildLegacyConfig = (amount, tokenAmount, onSuccess, onError) => ({
    amount,
    referencePrefix: "tokens",
    metadata: {
        token_amount: tokenAmount,
    },
    customFields: [
        {
            display_name: "Token Purchase",
            variable_name: "token_purchase",
            value: `${tokenAmount} tokens`,
        },
    ],
    contextData: tokenAmount,
    onSuccess: (response) => onSuccess?.(response, tokenAmount),
    onError,
});

export const usePaystack = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const initializePayment = useCallback(
        async (...args) => {
            const config = typeof args[0] === "object"
                ? args[0] || {}
                : buildLegacyConfig(...args);

            const {
                amount,
                currency = "ZAR",
                referencePrefix = "payment",
                metadata = {},
                customFields = [],
                contextData = null,
                onSuccess,
                onError,
            } = config;

            if (!user) {
                const authError = "User not authenticated";
                setError(authError);
                onError?.(authError);
                return;
            }

            const parsedAmount = Number(amount);
            if (!parsedAmount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
                const amountError = "Invalid payment amount";
                setError(amountError);
                onError?.(amountError);
                return;
            }

            const paystackKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
            if (!paystackKey) {
                const keyError = "Paystack public key is not configured";
                setError(keyError);
                onError?.(keyError);
                return;
            }

            const scriptReady = await ensurePaystackScript();
            if (!scriptReady || !window.PaystackPop) {
                const sdkError = "Paystack SDK is unavailable";
                setError(sdkError);
                onError?.(sdkError);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const handler = window.PaystackPop.setup({
                    key: paystackKey,
                    email: user.email,
                    amount: Math.round(parsedAmount * 100),
                    currency,
                    ref: `${referencePrefix}_${user.id}_${Date.now()}`,
                    metadata: {
                        user_id: user.id,
                        ...metadata,
                        custom_fields: [
                            {
                                display_name: "Employer",
                                variable_name: "user_id",
                                value: user.id,
                            },
                            ...customFields,
                        ],
                    },
                    callback(response) {
                        onSuccess?.(response, contextData);
                    },
                    onClose() {
                        onError?.("Payment was cancelled");
                    },
                });

                handler.openIframe();
            } catch (err) {
                const message = err instanceof Error ? err.message : "Payment initialisation failed";
                setError(message);
                onError?.(message);
            } finally {
                setLoading(false);
            }
        },
        [user],
    );

    return {
        initializePayment,
        loading,
        error,
    };
};
